/**
 * Workspace Settings → Integrations (#1153, Slice 1 of meta #1152).
 *
 * The combined Integrations tab. Replaces the prior CI/CD + Notifications
 * split: one screen, two provider cards stacked (GitHub on top, Slack
 * below). Each card carries only the workspace-level concern — the
 * connection lifecycle (install / re-authorize / disconnect) plus a
 * read-only "Used by apps in this workspace" oversight list.
 *
 * What does NOT live here anymore:
 *   - Per-repo trigger-rule editor — moved to the App Integrations
 *     tab's per-variant drawer (post-#1184; previously the standalone
 *     variant Automation page).
 *   - Per-app GitHub repo binding / Slack default channel — moved to
 *     each app's Integrations tab (Slice 2 of meta #1152).
 *   - Per-channel Slack routing matrix — moved alongside trigger rules
 *     into the App Integrations tab's per-variant drawer (post-#1184).
 *
 * What survives here:
 *   - The GitHub install / re-authorize / disconnect flow. Since the one
 *     GitHub card (docs/mockups/github-one-card.html) it lives inside the
 *     issue-tracker card for GitHub: this page loads the install with
 *     `useGitHubInstall` and hands its parts in as an `installSlot`.
 *   - The Slack install / disconnect flow (one Slack install per
 *     workspace per the meta — multiple-install support is a leftover
 *     from the prior model and rendering follows the same shape).
 *   - The reveal-once secret banner for webhook integrations + the
 *     OAuth callback `?github=<code>` / `?slackError=<code>` status
 *     banners (carried over from the CiIntegrations and Notifications
 *     pages so in-flight callbacks still land cleanly).
 *
 * Card rendering. Each provider card renders unconditionally — connected
 * or not — so the customer always sees both cards as siblings and can
 * tell at a glance which providers their workspace has wired up. The
 * card body branches on the connection state: connected → details +
 * Used By; disconnected → "Connect Provider" CTA.
 *
 * Why ONE page, not two surfaces sharing a parent. The mockup
 * (docs/mockups/three-tier-integrations.html Step 01) pins the two
 * cards as visual siblings so the operator sees them in one screen.
 * Splitting them across two tabs (the pre-#1153 design) was the
 * problem — operators looking for "Slack" had to remember it lived
 * under Notifications, not Integrations.
 *
 * Collapsible cards (#1165, bug sweep). Each provider card wraps its
 * body in a native `<details>` element so the operator can collapse
 * the provider they don't care about today. The default-open state
 * follows the heuristic in `computeDefaultExpanded`:
 *   - Both connected → both collapsed (the connection details are
 *     reference; the operator usually doesn't need them).
 *   - Only one connected → that one collapsed, the other expanded
 *     (the unconnected one's connect CTA is the call to action).
 *   - Neither connected → both expanded (the empty-state onboarding
 *     view; both connect CTAs visible at once).
 * The default is computed once when connection state first resolves;
 * after that the operator's manual toggle wins so a mid-session
 * disconnect doesn't snap-collapse the card they're looking at.
 * No persistence across visits — local component state by design.
 * Native `<details>` keeps it dep-free and matches the file's
 * hand-rolled primitive style (Tooltip etc.).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Copy, Check, KeyRound, ExternalLink,
} from 'lucide-react'
import {
  Button,
  Input,
  Label,
  Select,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@/components/ui'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { LockedCapability } from '@/components/LockedCapability'
import { useOrgEntitlements } from '@/hooks/useOrgEntitlements'
import type { WorkspaceSummary } from '@/lib/api'
import {
  ciIntegrationsAPI,
  slackAPI,
  applicationsAPI,
  type CiIntegration,
  type CiIntegrationWithSecret,
  type CiIntegrationProvider,
  type CreateCiIntegrationInput,
  type SlackInstallSummary,
  type AppIntegrationsResponse,
} from '@/lib/api'
import type { Application } from '@/types'
import { useGitHubInstall, InstallRepositories, installSubtitle, githubAppInstallUrl } from './GitHubInstall'
import { WebhookIntegrationBlock } from './WebhookIntegrationBlock'
import { ProviderCardShell } from './ProviderCardShell'
import { IssueTrackerCard, type AppInstallState, type InstallSlot } from './IssueTrackerCard'

// ── Status-banner copy (carried over from CiIntegrations + Notifications) ──
//
// The OAuth callbacks land here with `?github=<code>` or `?slackError=<code>`
// query params. Mapping each code to a contextual customer-facing message
// (so the operator knows the next step) was finalized in #1123 and is
// retained verbatim — the consolidation in #1153 only swaps which page
// renders the banners.

/** Banner copy for each `?github=<code>` status the OAuth callback can
 * land here with. New backend codes outside the map fall through to the
 * legacy generic `error` bucket so an older frontend deployed against a
 * newer backend still surfaces SOMETHING rather than rendering nothing. */
const GITHUB_STATUS_MESSAGES: Record<string, { kind: 'success' | 'error'; text: string }> = {
  connected: {
    kind: 'success',
    text: 'GitHub connected. Finished test runs will post a check on the matching pull request.',
  },
  'error-not-configured': {
    kind: 'error',
    text: "GitHub integration isn't set up on this installation. Ask whoever runs OttoTester here to turn it on.",
  },
  'error-expired-state': {
    kind: 'error',
    text: 'The install link expired. Click Connect GitHub again to restart.',
  },
  'error-user-mismatch': {
    kind: 'error',
    text:
      'This connection was started by another account. Sign in as the account that initiated the install, or click Connect GitHub again from this account.',
  },
  'error-missing-permission': {
    kind: 'error',
    text: 'You no longer have permission to manage integrations for this workspace.',
  },
  'error-installation-missing': {
    kind: 'error',
    text: "GitHub didn't return an installation id. Try Connect GitHub again.",
  },
  'error-unknown': {
    kind: 'error',
    text:
      'Something went wrong completing the install. Try again. If it keeps failing, ask whoever runs OttoTester here to check the server log.',
  },
  error: {
    kind: 'error',
    text: "Couldn't complete the GitHub connection. Try connecting again.",
  },
}

/** Provider options for the "Add provider" dialog (the legacy webhook
 * path is still customer-accessible for non-GitHub CI providers that
 * don't have a first-class block yet). */
const PROVIDER_OPTIONS: Array<{ value: CiIntegrationProvider; label: string; disabled?: boolean }> = [
  { value: 'webhook',     label: 'Generic webhook' },
  { value: 'gitlab',      label: 'GitLab (coming soon)',        disabled: true },
  { value: 'azureDevops', label: 'Azure DevOps (coming soon)',  disabled: true },
]

/** Inline status banner shape for the Slack OAuth callback. */
type SlackInstallStatus =
  | { kind: 'ok' }
  | { kind: 'cancelled' }
  | { kind: 'error'; code: string; description?: string }

function parseSlackStatus(params: URLSearchParams): SlackInstallStatus {
  if (params.get('slackCancelled') === '1') return { kind: 'cancelled' }
  const errorCode = params.get('slackError')
  if (errorCode) {
    return {
      kind: 'error',
      code: errorCode,
      description: params.get('slackErrorDescription') ?? undefined,
    }
  }
  return { kind: 'ok' }
}

function describeSlackError(
  code: string,
  description: string | undefined,
): { message: string; detail?: string } {
  switch (code) {
    case 'error-not-configured':
      return { message: "Slack integration isn't set up on this installation. Ask whoever runs OttoTester here to turn it on." }
    case 'error-expired-state':
      return { message: 'The install link expired. Click Connect to Slack again to restart.' }
    case 'error-user-mismatch':
      return {
        message:
          'This connection was started by another account. Sign in as the account that initiated the install, or click Connect to Slack again from this account.',
      }
    case 'error-missing-permission':
      return { message: 'You no longer have permission to manage integrations for this workspace.' }
    case 'error-unknown':
      return {
        message: 'Something went wrong completing the install. Try again. If it keeps failing, ask whoever runs OttoTester here to check the server log.',
        detail: description,
      }
    default:
      // Slack-side error (verbatim from the OAuth response).
      return { message: 'Slack rejected the connection.', detail: description ?? code }
  }
}

/**
 * Default-open state for the provider cards (#1165). Hand-rolls the
 * heuristic in the file header:
 *   - both connected → both collapsed
 *   - only one connected → connected one collapsed, other expanded
 *   - neither connected → both expanded (empty-state onboarding)
 *
 * Pure so it's trivially unit-testable.
 */
export function computeDefaultExpanded(
  githubConnected: boolean,
  slackConnected: boolean,
): { github: boolean; slack: boolean } {
  return {
    github: !githubConnected,
    slack: !slackConnected,
  }
}

// ── Component ────────────────────────────────────────────────────────────

export function WorkspaceIntegrations({ workspace }: { workspace: WorkspaceSummary }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const githubStatus = searchParams.get('github')
  const slackStatus = useMemo(() => parseSlackStatus(searchParams), [searchParams])
  const githubBanner = githubStatus
    ? (GITHUB_STATUS_MESSAGES[githubStatus] ?? GITHUB_STATUS_MESSAGES.error)
    : null

  // ── CI integration state (covers GitHub + webhook) ──
  const [integrations, setIntegrations] = useState<CiIntegration[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [freshSecret, setFreshSecret] = useState<{
    id: string
    secret: string
    context: 'created' | 'rotated'
  } | null>(null)
  const [copied, setCopied] = useState(false)

  // ── Slack install state ──
  const [slackInstalls, setSlackInstalls] = useState<SlackInstallSummary[] | null>(null)
  const [slackLoadError, setSlackLoadError] = useState<string | null>(null)
  const [slackOAuthInFlight, setSlackOAuthInFlight] = useState(false)
  const [slackToast, setSlackToast] = useState<
    | { kind: 'success'; message: string }
    | { kind: 'error'; message: string; detail?: string }
    | null
  >(null)
  const [disconnectSlackTarget, setDisconnectSlackTarget] = useState<SlackInstallSummary | null>(null)
  const [disconnectingSlack, setDisconnectingSlack] = useState(false)

  // ── "Used by apps" oversight state ──
  // Aggregated from `applicationsAPI.getAll()` + per-app `getIntegrations()`
  // so the workspace surface shows, per provider, every app and its
  // binding state. Loaded asynchronously: cards render the connection
  // details first, the oversight slides in after.
  const [oversight, setOversight] = useState<WorkspaceUsedByOversight | null>(null)
  const [oversightError, setOversightError] = useState<string | null>(null)

  // Slack entitlement — Slack notifications are plan-gated; render the
  // existing `LockedCapability` (matches the pre-#1153 Notifications
  // page semantics) when the org's plan doesn't include the feature.
  const orgId = (workspace as { orgId?: string }).orgId
  const { hasFeature, loading: entitlementsLoading } = useOrgEntitlements(orgId)
  const hasSlack = hasFeature('integration.slack')

  // ── Loaders ──
  const reloadCi = useCallback(async () => {
    try {
      const data = await ciIntegrationsAPI.list()
      setIntegrations(data)
      setLoadError(null)
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load CI integrations')
    }
  }, [])

  const reloadSlack = useCallback(async () => {
    if (!hasSlack) {
      // Locked plan — skip the fetch, render the LockedCapability slot.
      setSlackInstalls([])
      return
    }
    try {
      const data = await slackAPI.listInstalls(workspace._id)
      setSlackInstalls(data)
      setSlackLoadError(null)
    } catch (err) {
      setSlackLoadError(err instanceof Error ? err.message : 'Failed to load Slack installs')
    }
  }, [workspace._id, hasSlack])

  const reloadOversight = useCallback(async () => {
    try {
      const apps = await applicationsAPI.getAll()
      // Fan out per-app integrations fetches in parallel. N is small
      // (single-digit apps per workspace in practice); the GET handler
      // is server-cached via the existing applicationRepo query and
      // returns the per-variant counts we then sum per app.
      const perAppResults = await Promise.allSettled(
        apps.map((app) =>
          applicationsAPI.getIntegrations(getAppId(app)).then((res) => ({ app, res })),
        ),
      )
      // Map every app — including ones whose per-app fetch rejected —
      // into an oversight row. A rejected app surfaces as an
      // unknown-binding row (no repo / no channel / no counts) with
      // `fetchFailed: true` so the per-row renderer can show a
      // muted "unable to load" hint instead of silently dropping the
      // app. Without this, a single-app endpoint blip would erase that
      // app from the oversight list entirely (Copilot review on #1162)
      // — the operator would see "Marketing doesn't appear" and wrongly
      // conclude it was deleted, when really it just hadn't loaded.
      const rows: WorkspaceUsedByRow[] = perAppResults.map((result, idx) => {
        const app = apps[idx]!
        if (result.status === 'rejected') {
          return {
            appId: getAppId(app),
            appName: app.name,
            fetchFailed: true,
            github: {
              repoIdentifier: undefined,
              ruleCount: 0,
              variantCount: 0,
              variantsWithRules: [],
            },
            slack: {
              defaultChannelId: undefined,
              defaultChannelName: undefined,
              overrideCount: 0,
              variantsWithOverrides: [],
            },
          }
        }
        const { res } = result.value
        const githubCount = res.oversight.github.reduce((s, row) => s + row.count, 0)
        const githubVariantsWithRules = res.oversight.github
          .filter((row) => row.count > 0)
          .map((row) => ({ variantId: row.variantId, variantName: row.variantName }))
        const slackCount = res.oversight.slack.reduce((s, row) => s + row.count, 0)
        const slackVariantsWithOverrides = res.oversight.slack
          .filter((row) => row.count > 0)
          .map((row) => ({ variantId: row.variantId, variantName: row.variantName }))
        return {
          appId: getAppId(app),
          appName: app.name,
          fetchFailed: false,
          github: {
            repoIdentifier: res.bindings.github?.repoIdentifier,
            ruleCount: githubCount,
            variantCount: githubVariantsWithRules.length,
            // #1165 — kept so the "Open" link can deep-link to the
            // single variant's drawer in the App Integrations tab
            // when there's exactly one variant with rules (the common
            // case in dogfooding). Multi-variant apps fall back to
            // the app's Integrations tab; zero-variant apps suppress
            // the "Open" link entirely. (Post-#1184 the destination
            // is the per-variant drawer on the App Integrations tab,
            // not the deleted standalone Automation page.)
            variantsWithRules: githubVariantsWithRules,
          },
          slack: {
            defaultChannelId: res.bindings.slack?.defaultChannelId,
            defaultChannelName: resolveSlackChannelName(
              res.bindings.slack?.defaultChannelId,
              res,
            ),
            overrideCount: slackCount,
            // #1165 — same pattern as `variantsWithRules`; the Slack
            // card's "Open" routes to the single overriding variant's
            // drawer on the App Integrations tab when applicable.
            variantsWithOverrides: slackVariantsWithOverrides,
          },
        }
      })
      setOversight({ apps: rows })
      setOversightError(null)
    } catch (err) {
      setOversightError(
        err instanceof Error ? err.message : 'Failed to load app oversight',
      )
    }
  }, [])

  useEffect(() => { void reloadCi() }, [reloadCi])
  useEffect(() => { void reloadSlack() }, [reloadSlack])
  useEffect(() => { void reloadOversight() }, [reloadOversight])

  // Strip Slack OAuth callback params on mount (mirrors the prior
  // Notifications page behavior so reloads don't replay the toast).
  useEffect(() => {
    if (slackStatus.kind === 'cancelled') {
      searchParams.delete('slackCancelled')
      setSearchParams(searchParams, { replace: true })
    } else if (slackStatus.kind === 'error') {
      const described = describeSlackError(slackStatus.code, slackStatus.description)
      setSlackToast({ kind: 'error', message: described.message, detail: described.detail })
      searchParams.delete('slackError')
      searchParams.delete('slackErrorDescription')
      setSearchParams(searchParams, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handleCopy(): Promise<void> {
    if (!freshSecret) return
    try {
      await navigator.clipboard.writeText(freshSecret.secret)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard API unavailable — value visible in the readonly Input.
    }
  }

  const handleConnectSlack = useCallback(async () => {
    setSlackOAuthInFlight(true)
    try {
      const { url } = await slackAPI.initiateOAuth(workspace._id)
      window.location.assign(url)
    } catch (e) {
      const err = e as Error & { code?: string; upgradeHint?: string }
      setSlackToast({
        kind: 'error',
        message: err.upgradeHint ?? err.message ?? 'Failed to start Slack connection',
      })
      setSlackOAuthInFlight(false)
    }
  }, [workspace._id])

  const handleDisconnectSlack = useCallback(async () => {
    if (!disconnectSlackTarget) return
    setDisconnectingSlack(true)
    try {
      await slackAPI.disconnectInstall(workspace._id, disconnectSlackTarget._id)
      setSlackToast({
        kind: 'success',
        message: `Disconnected ${disconnectSlackTarget.slackTeamName}.`,
      })
      setDisconnectSlackTarget(null)
      await reloadSlack()
    } catch (e) {
      setSlackToast({
        kind: 'error',
        message: 'Failed to disconnect',
        detail: e instanceof Error ? e.message : String(e),
      })
    } finally {
      setDisconnectingSlack(false)
    }
  }, [disconnectSlackTarget, workspace._id, reloadSlack])

  const githubIntegrations = integrations?.filter((i) => i.provider === 'github') ?? []
  const webhookIntegrations = integrations?.filter((i) => i.provider === 'webhook') ?? []
  const githubConnected = githubIntegrations.length > 0
  // The GitHub App install, for the issue-tracker card: unknown while the
  // integrations load, none when GitHub isn't connected.
  const appInstall: AppInstallState = integrations === null
    ? undefined
    : (() => {
        const raw = githubIntegrations[0]?.providerConfig?.installationId
        const id = typeof raw === 'number' ? raw : typeof raw === 'string' && /^\d+$/.test(raw) ? parseInt(raw, 10) : null
        return id ? { installationId: id } : null
      })()
  const slackConnected = (slackInstalls?.length ?? 0) > 0

  // The GitHub App install, for the one GitHub card. Loaded here because
  // this page owns the install record; the card renders it.
  const githubInstall = useGitHubInstall(workspace._id, appInstall?.installationId ?? null)
  // Bumped when the page removes connections itself, so the card reloads.
  const [trackerReload, setTrackerReload] = useState(0)
  const disconnectGitHub = useCallback(async () => {
    try {
      await ciIntegrationsAPI.disconnectGitHub()
    } finally {
      // Partway or not, both lists may have changed.
      setTrackerReload((n) => n + 1)
      await reloadCi()
    }
  }, [reloadCi])
  const [removeBrokenOpen, setRemoveBrokenOpen] = useState(false)
  const installSlot: InstallSlot = {
    repositories: <InstallRepositories install={githubInstall} onRemove={() => setRemoveBrokenOpen(true)} />,
    usedBy: (
      <UsedByAppsSection
        provider="github"
        workspaceId={workspace._id}
        oversight={oversight}
        oversightError={oversightError}
      />
    ),
    menuItems: githubInstall.menuItems,
    installLine: installSubtitle(githubInstall),
    installOnlyAbility: 'Post checks on pull requests',
    installPurpose: 'posting checks on pull requests',
    disconnectAll: disconnectGitHub,
    install: () => window.location.assign(githubAppInstallUrl(workspace._id)),
    lookup: {
      status: githubInstall.status,
      account: githubInstall.details?.account ?? null,
      issuesPermission: githubInstall.details?.permissions?.issues ?? (githubInstall.details?.permissions ? 'none' : null),
      refresh: githubInstall.refresh,
    },
  }

  // #1165 — collapsible-cards default state. Initialized ONCE when
  // both providers' connection state has first resolved (or the Slack
  // entitlement is known to be locked, which counts as resolved for
  // the heuristic — treat "locked" as "not connected" so the GitHub
  // card alone gets the both-connected collapse behavior). After init,
  // the operator's toggles win — a disconnect mid-session doesn't
  // snap-open the card they just closed.
  const [expanded, setExpanded] = useState<{ github: boolean; slack: boolean } | null>(null)
  const initRef = useRef(false)
  const slackResolved =
    entitlementsLoading === false && (!hasSlack || slackInstalls !== null || slackLoadError !== null)
  useEffect(() => {
    if (initRef.current) return
    if (integrations === null) return
    if (!slackResolved) return
    initRef.current = true
    setExpanded(computeDefaultExpanded(githubConnected, slackConnected))
  }, [integrations, slackResolved, githubConnected, slackConnected])
  const slackExpanded = expanded?.slack ?? true
  const setSlackExpanded = (next: boolean) =>
    setExpanded((prev) => ({ ...(prev ?? { github: true, slack: true }), slack: next }))

  // Compact one-line summary rendered next to the provider name in the
  // collapsed-card header. Keeps the operator oriented about the
  // connection state without expanding.
  const slackSummary: ReactNode = !hasSlack ? (
    <span className="text-muted-foreground">Plan-gated</span>
  ) : slackConnected ? (
    <span className="text-emerald-700 dark:text-emerald-300">
      Connected{slackInstalls && slackInstalls[0]?.slackTeamName ? ` · ${slackInstalls[0].slackTeamName}` : ''}
    </span>
  ) : (
    <span className="text-muted-foreground">Not connected</span>
  )

  return (
    <div className="space-y-6 max-w-3xl">
      <header className="space-y-1">
        <h2 className="text-lg font-semibold">Integrations</h2>
        <p className="text-sm text-muted-foreground">
          External providers connected to this workspace. Manage each connection
          here; pick which repo and which default channel each app uses from
          the app's Integrations tab.
        </p>
      </header>

      {/* Reveal-once webhook secret banner. */}
      {freshSecret && (
        <div className="rounded border border-green-300 bg-green-50 dark:border-green-900 dark:bg-green-950 p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <KeyRound className="h-4 w-4" aria-hidden="true" />
            {freshSecret.context === 'created' ? 'New webhook secret' : 'Rotated webhook secret'}
            {' '}— copy it now
          </div>
          <p className="text-xs text-muted-foreground">
            Copy this now and paste it into your CI's webhook-secret field. This is the only
            time we'll show it — if you lose it you'll need to{' '}
            {freshSecret.context === 'created' ? 'delete and recreate' : 'rotate again'}{' '}
            the integration.{' '}
            <a
              href="/docs/ci-cd-integration"
              target="_blank"
              rel="noreferrer"
              className="underline hover:no-underline"
            >
              Learn more
            </a>
          </p>
          <div className="flex items-center gap-2">
            <Input
              readOnly
              value={freshSecret.secret}
              onFocus={(e) => e.currentTarget.select()}
              aria-label="Fresh webhook secret"
            />
            <Button onClick={() => void handleCopy()} variant="outline">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              <span className="ml-1.5">{copied ? 'Copied' : 'Copy'}</span>
            </Button>
          </div>
          <div>
            <Button variant="ghost" size="sm" onClick={() => { setFreshSecret(null); setCopied(false) }}>
              Dismiss
            </Button>
          </div>
        </div>
      )}

      {loadError && <p className="text-sm text-destructive" role="alert">{loadError}</p>}
      {actionError && <p className="text-sm text-destructive" role="alert">{actionError}</p>}
      {githubBanner && githubBanner.kind === 'success' && (
        <p className="text-sm text-green-700 dark:text-green-300" role="status">{githubBanner.text}</p>
      )}
      {githubBanner && githubBanner.kind === 'error' && (
        <p className="text-sm text-destructive" role="alert">{githubBanner.text}</p>
      )}
      {slackToast && (
        <SlackInlineToast toast={slackToast} onDismiss={() => setSlackToast(null)} />
      )}

      {/* ── Slack provider card ──────────────────────────────────────── */}
      <section aria-labelledby="slack-card-heading">
        <h3 id="slack-card-heading" className="sr-only">Slack</h3>
        {entitlementsLoading ? (
          <CardSkeleton />
        ) : !hasSlack ? (
          // Plan-locked state: render the LockedCapability inside the
          // shell so the visual hierarchy matches the connected /
          // unconnected cases and the operator can collapse the
          // upsell to focus on GitHub.
          <ProviderCardShell
            headingId="slack-card-shell"
            providerName="Slack"
            summary={slackSummary}
            open={slackExpanded}
            onOpenChange={setSlackExpanded}
          >
            <LockedCapability
              compact
              featureName="Slack notifications"
              description="Route run failures, regression alerts, scheduled-run failures, and cost-ceiling alerts to Slack channels. One install per Slack workspace; many channels per install."
              entitlingPlans={['Business', 'Unlimited', 'Enterprise']}
            />
          </ProviderCardShell>
        ) : slackInstalls === null && !slackLoadError ? (
          <CardSkeleton />
        ) : slackLoadError ? (
          <p className="text-sm text-destructive" role="alert">{slackLoadError}</p>
        ) : (
          <ProviderCardShell
            headingId="slack-card-shell"
            providerName="Slack"
            summary={slackSummary}
            open={slackExpanded}
            onOpenChange={setSlackExpanded}
          >
            {!slackConnected ? (
              <SlackEmptyCard onConnect={handleConnectSlack} oauthInFlight={slackOAuthInFlight} />
            ) : (
              (slackInstalls ?? []).map((install) => (
                <div key={install._id} className="space-y-4">
                  <SlackConnectedCard
                    install={install}
                    onDisconnect={() => setDisconnectSlackTarget(install)}
                  />
                  <UsedByAppsSection
                    provider="slack"
                    workspaceId={workspace._id}
                    oversight={oversight}
                    oversightError={oversightError}
                  />
                </div>
              ))
            )}
          </ProviderCardShell>
        )}
      </section>

      {/* ── Issue-tracker card (#1558 Slice 1a) ───────────────────────── */}
      {/* The workspace-level connection for inbound requirement/defect
          coverage. Self-contained (loads its own connection + descriptors)
          and sibling to the GitHub/Slack cards — credentials live once at
          the workspace, exactly like the GitHub install. */}
      {/* The one GitHub card: the tracker that signs in through the GitHub
          App also shows the install, its menu and the apps using it. */}
      <IssueTrackerCard workspaceId={workspace._id} appInstall={appInstall} installSlot={installSlot} reloadKey={trackerReload} />
      <ConfirmDialog
        open={removeBrokenOpen}
        onClose={() => setRemoveBrokenOpen(false)}
        title="Remove GitHub?"
        description="The GitHub App is no longer installed. Removing it here stops checks and issues until you connect GitHub again."
        confirmLabel="Remove"
        confirmVariant="destructive"
        onConfirm={async () => {
          try {
            await disconnectGitHub()
          } catch (err) {
            setActionError(err instanceof Error ? err.message : 'Failed to disconnect GitHub')
          } finally {
            setRemoveBrokenOpen(false)
          }
        }}
      />

      {/* Webhook provider cards (non-first-class CI providers — listed
          below the two primary cards so the visual hierarchy stays
          "GitHub + Slack first, miscellaneous webhooks second"). The
          rotate / disconnect lifecycle is identical to the pre-#1153
          rendering. */}
      {webhookIntegrations.length > 0 && (
        <section aria-labelledby="webhook-cards-heading" className="space-y-3">
          <h3 id="webhook-cards-heading" className="text-sm font-medium text-muted-foreground">
            Other CI providers
          </h3>
          {webhookIntegrations.map((integration) => (
            <WebhookIntegrationBlock
              key={integration.id}
              integration={integration}
              onRotated={(result) => {
                void reloadCi()
                setFreshSecret({ id: result.id, secret: result.secret, context: 'rotated' })
              }}
              onDeleted={() => void reloadCi()}
              onError={setActionError}
            />
          ))}
        </section>
      )}

      {integrations !== null && (
        <div className="flex justify-end">
          <Button size="sm" variant="outline" onClick={() => setCreating(true)}>
            + Add another CI provider
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={!!disconnectSlackTarget}
        onClose={() => setDisconnectSlackTarget(null)}
        title="Disconnect Slack?"
        description={
          disconnectSlackTarget ? (
            <>
              Disconnecting <strong>{disconnectSlackTarget.slackTeamName}</strong> will
              also remove every channel destination under it. The OAuth token we
              hold will be discarded; future deliveries to this Slack workspace
              will stop until you reconnect.
            </>
          ) : (
            ''
          )
        }
        confirmLabel="Disconnect"
        confirmVariant="destructive"
        onConfirm={handleDisconnectSlack}
        busy={disconnectingSlack}
      />

      <AddProviderDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(result) => {
          setCreating(false)
          void reloadCi()
          if ('secret' in result && (result as CiIntegrationWithSecret).secretRevealedOnce) {
            setFreshSecret({
              id: result.id,
              secret: (result as CiIntegrationWithSecret).secret,
              context: 'created',
            })
          }
        }}
        onError={setActionError}
      />
    </div>
  )
}

// ── "Used by apps" oversight ──────────────────────────────────────────────

interface WorkspaceUsedByRow {
  appId: string
  appName: string
  /** True when the per-app `getIntegrations` fetch rejected. The row
   * still renders so the operator sees every workspace app, but the
   * binding cells render a muted "unable to load" hint instead of
   * the bound repo / channel / counts. Set per Copilot review on
   * #1162 — silently dropping a failed app would let an intermittent
   * single-app endpoint blip erase that app from the oversight list,
   * which reads as "the app was deleted." */
  fetchFailed: boolean
  github: {
    repoIdentifier: string | undefined
    /** Total trigger rules across all variants of this app. */
    ruleCount: number
    /** Variants of this app with at least one rule (used to render
     * "N rules across M variants" in the oversight row). */
    variantCount: number
    /** Per-variant detail for the variants that have at least one
     * rule. Length === `variantCount`. When length === 1 the "Open"
     * affordance deep-links to that variant's drawer on the App
     * Integrations tab directly; otherwise it falls back to the
     * app's Integrations tab. #1165 (post-#1184 drawer surface). */
    variantsWithRules: Array<{ variantId: string; variantName: string }>
  }
  slack: {
    defaultChannelId: string | undefined
    /** Pretty channel name (with `#` prefix) if resolvable from the
     * workspace's channel list. Undefined when the channel was deleted
     * Slack-side or hasn't been resolved yet — the row falls back to
     * the raw channel id in that case. */
    defaultChannelName: string | undefined
    /** Per-variant override count. */
    overrideCount: number
    /** Per-variant detail for the variants that have at least one
     * notification override. Same use as `variantsWithRules` on the
     * GitHub side. #1165. */
    variantsWithOverrides: Array<{ variantId: string; variantName: string }>
  }
}

interface WorkspaceUsedByOversight {
  apps: WorkspaceUsedByRow[]
}

/** Per-card oversight section — the "Used by apps in this workspace"
 * block rendered below each provider card. Surfaces per-app binding
 * state and counts; apps with no binding render muted with a
 * "Configure →" link into that app's Integrations tab. Mirrors the
 * `OversightSection` shape in `AppIntegrationsTab.tsx` (Slice 2) so
 * the two surfaces feel like one family — Slice 2's "Variants using
 * this binding" is per-variant; this one is per-app. */
function UsedByAppsSection({
  provider,
  workspaceId,
  oversight,
  oversightError,
}: {
  provider: 'github' | 'slack'
  workspaceId: string
  oversight: WorkspaceUsedByOversight | null
  oversightError: string | null
}) {
  if (oversightError) {
    return (
      <div className="rounded-lg border bg-card p-4">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
          Used by apps in this workspace
        </p>
        <p className="text-xs text-destructive">{oversightError}</p>
      </div>
    )
  }
  if (!oversight) {
    return (
      <div className="rounded-lg border bg-card p-4">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
          Used by apps in this workspace
        </p>
        <p className="text-xs text-muted-foreground">Loading apps…</p>
      </div>
    )
  }
  if (oversight.apps.length === 0) {
    return (
      <div className="rounded-lg border bg-card p-4">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
          Used by apps in this workspace
        </p>
        <p className="text-xs text-muted-foreground">
          No apps yet — create one to start binding integrations.
        </p>
      </div>
    )
  }
  return (
    <div className="rounded-lg border bg-card p-4">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
        Used by apps in this workspace
      </p>
      <ul className="space-y-1.5">
        {oversight.apps.map((row) => (
          <UsedByAppRow
            key={row.appId}
            provider={provider}
            row={row}
            workspaceId={workspaceId}
          />
        ))}
      </ul>
    </div>
  )
}

function UsedByAppRow({
  provider,
  row,
  workspaceId,
}: {
  provider: 'github' | 'slack'
  row: WorkspaceUsedByRow
  workspaceId: string
}) {
  // The per-app Integrations tab is mounted at the Applications page.
  // #1165 — earlier this href was
  // `/workspaces/<wid>/applications/<appId>?tab=integrations`, but
  // the Applications route doesn't take an `:appId` path segment;
  // the unmatched URL fell through to the workspace's index
  // `<Navigate to="runner" />`, which is why the customer observed
  // clicks landing on Start. The fix routes via the working
  // `?app=<id>&tab=integrations` query shape — Applications now reads
  // `?app=` and focuses that app on mount (sibling change in this PR).
  const appHref = `/workspaces/${workspaceId}/applications?app=${encodeURIComponent(row.appId)}&tab=integrations`

  const isGithub = provider === 'github'
  // Per-variant deep link when the operator clearly intends to inspect
  // a specific variant's rules / overrides. The workspace oversight
  // row is per-APP, but when there's exactly one variant with rules
  // (or overrides) on this app we know which variant the operator
  // means and can drop them straight into the App Integrations tab's
  // per-variant drawer with the right variant pre-selected (the
  // surface #1184 carved when it retired the standalone Automation
  // page). Zero-variant case: there's nothing to "Open" — the link
  // suppresses. Multi-variant case: fall back to the App's
  // Integrations tab where the per-variant OversightSection lets the
  // operator pick the variant they want.
  const variants = isGithub ? row.github.variantsWithRules : row.slack.variantsWithOverrides
  const singleVariantDrawerHref =
    variants.length === 1
      ? `/workspaces/${workspaceId}/applications?app=${encodeURIComponent(row.appId)}&tab=integrations&variantConfig=${encodeURIComponent(variants[0]!.variantId)}&variantConfigKind=${isGithub ? 'trigger' : 'slack'}`
      : null
  const binding = isGithub ? row.github.repoIdentifier : row.slack.defaultChannelId
  // The row reads as "muted" both when the app legitimately has no
  // binding AND when the binding fetch failed — both render with the
  // de-emphasized text color. The middle cell distinguishes the two
  // cases (italic "no repo selected" vs italic "unable to load") so
  // the operator can tell apart "needs configuring" from "transient
  // blip, try again."
  const isMuted = !binding || row.fetchFailed

  // Plain-language oversight strings. GitHub side: "N rules across M
  // variants" reads better than "N TriggerRule entities scoped to M
  // variantIds." Slack side: "N variant overrides" mirrors the noun
  // split AppIntegrationsTab.tsx settled on (rules vs overrides).
  // Counts hide on the failed-fetch row since they're not real (the
  // row defaults to zero on rejection, which would mislead).
  const ghStat = isGithub && !row.fetchFailed
    ? row.github.ruleCount === 0
      ? null
      : row.github.variantCount > 1
        ? `${row.github.ruleCount} trigger rule${row.github.ruleCount === 1 ? '' : 's'} across ${row.github.variantCount} variants`
        : `${row.github.ruleCount} trigger rule${row.github.ruleCount === 1 ? '' : 's'}`
    : null
  const slackStat = !isGithub && !row.fetchFailed
    ? row.slack.overrideCount === 0
      ? 'app default'
      : `${row.slack.overrideCount} variant override${row.slack.overrideCount === 1 ? '' : 's'}`
    : null

  return (
    <li className={isMuted ? 'flex items-center gap-2 text-xs text-muted-foreground' : 'flex items-center gap-2 text-xs'}>
      <span className={isMuted ? '' : 'font-medium'}>{row.appName}</span>
      <span className="text-muted-foreground">→</span>
      {row.fetchFailed ? (
        <em>unable to load — try again</em>
      ) : isMuted ? (
        <em>
          {isGithub ? 'no repo selected' : 'no default channel set'}
        </em>
      ) : (
        <span className="font-mono text-muted-foreground">
          {isGithub
            ? row.github.repoIdentifier
            : (row.slack.defaultChannelName ?? row.slack.defaultChannelId)}
        </span>
      )}
      <span className="ml-auto inline-flex items-center gap-2">
        {!isMuted && (isGithub ? ghStat : slackStat) && (
          <span className="text-muted-foreground">{isGithub ? ghStat : slackStat}</span>
        )}
        {/* Suppress the muted "Configure" CTA on rejected fetches —
            the operator can't act on it until the fetch succeeds.
            Successful muted rows (legitimately no binding) keep the
            Configure CTA, which routes to the app's Integrations tab.
            Non-muted "Open" routes to the single variant's drawer on
            the App Integrations tab when exactly one variant carries
            rules / overrides (the common case in dogfooding), and to
            the app's
            Integrations tab otherwise (where the per-variant
            OversightSection lets the operator pick which variant
            they want). #1165. */}
        {!row.fetchFailed && (
          <Link
            to={
              isMuted
                ? appHref
                : (singleVariantDrawerHref ?? appHref)
            }
            className="text-primary hover:underline inline-flex items-center gap-0.5"
          >
            {isMuted ? 'Configure →' : (
              <>
                Open <ExternalLink className="h-3 w-3" />
              </>
            )}
          </Link>
        )}
      </span>
    </li>
  )
}

// ── Card skeleton ─────────────────────────────────────────────────────────

function CardSkeleton() {
  return (
    <div
      className="rounded-lg border border-border h-32 animate-pulse bg-muted/30"
      aria-hidden="true"
    />
  )
}


// ── Empty-state cards ─────────────────────────────────────────────────────

/** Empty state for the Slack card. CTA initiates the OAuth round-trip
 * via `slackAPI.initiateOAuth`. The post-callback wizard step (channel
 * picker) still lives at `notifications/slack/channels` — see
 * `App.tsx` for the route which is preserved across the #1153
 * consolidation specifically for this flow. */
function SlackEmptyCard({
  onConnect,
  oauthInFlight,
}: {
  onConnect: () => void
  oauthInFlight: boolean
}) {
  // #1165 — outer card chrome lives in ProviderCardShell, so this body
  // draws no border of its own.
  return (
    <div className="text-center py-2">
      <SlackLogoBlock className="mx-auto mb-3 h-10 w-10" />
      <h3 className="text-sm font-semibold">Connect Slack to route alerts to a channel</h3>
      <p className="mt-1 text-xs text-muted-foreground max-w-md mx-auto leading-relaxed">
        Your team will see test failures, regressions, scheduled-run failures, and
        cost-ceiling alerts in real time — no one has to log into OttoTester to know
        something broke.
      </p>
      <button
        type="button"
        onClick={onConnect}
        disabled={oauthInFlight}
        className="mt-3 inline-flex items-center gap-2 rounded-md bg-[#4a154b] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#5a1d5c] disabled:opacity-60"
      >
        <SlackLogoBlock className="h-3.5 w-3.5" />
        {oauthInFlight ? 'Redirecting…' : 'Connect to Slack'}
      </button>
    </div>
  )
}

/** Connected-state Slack card. Renders one install's connection details
 * + a Disconnect kebab. Channel routing (the per-channel rule editor
 * that lived on the prior Notifications page) is intentionally absent
 * — those affordances moved to each app's Integrations tab and into
 * the App Integrations tab's per-variant drawer (Slice 2 of meta
 * #1152 + Slice 1 of meta #1183).
 *
 * The "Add channel" / wizard flow still routes through the surviving
 * `notifications/slack/channels` sub-route — initiated via the OAuth
 * callback, not from here. */
function SlackConnectedCard({
  install,
  onDisconnect,
}: {
  install: SlackInstallSummary
  onDisconnect: () => void
}) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <header className="flex items-center gap-3 mb-3">
        <SlackLogoBlock className="h-8 w-8" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">Slack</h3>
            <span className="rounded-full bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900 px-2 py-0.5 text-[10px] font-semibold">
              Connected
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            {install.slackTeamDomain ? `${install.slackTeamDomain}.slack.com` : install.slackTeamName}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDisconnect}
          className="text-destructive hover:bg-destructive/10"
        >
          Disconnect
        </Button>
      </header>
      <dl className="grid grid-cols-[140px_1fr] gap-y-1 text-xs">
        <dt className="text-muted-foreground">Slack workspace</dt>
        <dd className="font-medium">{install.slackTeamName}</dd>
        {install.botUserId && (
          <>
            <dt className="text-muted-foreground">Bot user</dt>
            <dd className="font-mono text-[11px]">{install.botUserId}</dd>
          </>
        )}
        {install.scopes && install.scopes.length > 0 && (
          <>
            <dt className="text-muted-foreground">Scopes</dt>
            <dd className="font-mono text-[11px] text-muted-foreground">
              {install.scopes.join(', ')}
            </dd>
          </>
        )}
      </dl>
    </div>
  )
}

/** The four-colour Slack logo block, replicated in CSS gradients
 * (faithful to the prior Notifications page rendering). Used in the
 * empty state, the connected card, and the steady-state row. */
function SlackLogoBlock({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-block rounded-sm ${className ?? ''}`}
      style={{
        background:
          'linear-gradient(135deg,#36c5f0 0% 25%,#2eb67d 25% 50%,#ecb22e 50% 75%,#e01e5a 75% 100%)',
      }}
    />
  )
}

/** Inline toast for Slack OAuth callback status — carried over from
 * the prior Notifications page so the in-flight callback experience
 * is preserved across the #1153 consolidation. */
function SlackInlineToast({
  toast,
  onDismiss,
}: {
  toast:
    | { kind: 'success'; message: string }
    | { kind: 'error'; message: string; detail?: string }
  onDismiss: () => void
}) {
  const isError = toast.kind === 'error'
  return (
    <div
      role={isError ? 'alert' : 'status'}
      className={`flex items-start justify-between gap-3 rounded border px-3 py-2 text-sm ${
        isError
          ? 'border-destructive/50 bg-destructive/10 text-destructive'
          : 'border-green-300 bg-green-50 text-green-800 dark:border-green-900 dark:bg-green-950 dark:text-green-300'
      }`}
    >
      <div>
        <div>{toast.message}</div>
        {isError && (toast as { detail?: string }).detail && (
          <div className="mt-1 font-mono text-xs opacity-80">
            {(toast as { detail?: string }).detail}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="text-xs underline hover:no-underline"
      >
        Dismiss
      </button>
    </div>
  )
}

// ── Add CI provider dialog (carried over from CiIntegrations) ────────────

function AddProviderDialog({
  open, onClose, onCreated, onError,
}: {
  open: boolean
  onClose: () => void
  onCreated: (result: CiIntegration | CiIntegrationWithSecret) => void
  onError: (msg: string) => void
}) {
  const [provider, setProvider] = useState<CiIntegrationProvider>('webhook')
  const [callbackUrl, setCallbackUrl] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  function reset() {
    setProvider('webhook')
    setCallbackUrl('')
    setLocalError(null)
    setSubmitting(false)
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setLocalError(null)
    if (!callbackUrl.trim()) {
      setLocalError('Callback URL is required for the webhook provider.')
      return
    }
    setSubmitting(true)
    try {
      const input: CreateCiIntegrationInput = {
        provider,
        callbackUrl: callbackUrl.trim(),
      }
      const result = await ciIntegrationsAPI.create(input)
      reset()
      onCreated(result)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create integration'
      setLocalError(msg)
      onError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose() } }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add CI provider</DialogTitle>
          <DialogDescription>
            Connect a CI provider so finished test runs report back to it.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => void handleSubmit(e)}>
          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ci-provider">Provider</Label>
              <Select
                id="ci-provider"
                options={PROVIDER_OPTIONS}
                value={provider}
                onChange={(e) => setProvider(e.target.value as CiIntegrationProvider)}
              />
              <p className="text-xs text-muted-foreground">
                GitHub is connected in the GitHub card. Native GitLab and Azure DevOps
                integrations are coming soon. The generic webhook works with every other CI
                provider today.
              </p>
            </div>

            {(
              <div className="space-y-2">
                <Label htmlFor="ci-callback-url">Callback URL</Label>
                <Input
                  id="ci-callback-url"
                  type="url"
                  placeholder="https://ci.example.com/ottotester/webhook"
                  value={callbackUrl}
                  onChange={(e) => setCallbackUrl(e.target.value)}
                  disabled={submitting}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Use https:// in production for transport security. After creating, we'll
                  show you a signing secret to configure on your CI receiver.{' '}
                  <a
                    href="/docs/ci-cd-integration"
                    target="_blank"
                    rel="noreferrer"
                    className="underline hover:no-underline"
                  >
                    Setup guide <ExternalLink className="inline h-3 w-3" />
                  </a>
                </p>
              </div>
            )}
            {localError && (
              <p className="text-sm text-destructive" role="alert">{localError}</p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => { reset(); onClose() }}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ── Helpers ──────────────────────────────────────────────────────────────

/** Resolve the Application id from the API response shape, which
 * normalizes `_id` → `id` but keeps `_id` available as fallback for
 * any older shape that flows through. Mirrors `getAppId` in
 * `Notifications.tsx` (now retired) so the projection logic stays
 * stable across both call sites until full TS migration off the
 * legacy `_id` field. */
function getAppId(app: Application): string {
  const a = app as Application & { _id?: string; id?: string }
  return String(a.id ?? a._id ?? '')
}

/** Resolve a channel id → pretty name from the per-app integrations
 * response. The per-app GET aggregates the workspace's full channel
 * list (so the picker can render options); we project here to display
 * `#channel` instead of the opaque `C012345` id. Returns `undefined`
 * when the binding's channel id doesn't appear in the resolved list
 * (the channel was deleted Slack-side after the binding was set —
 * the row falls back to the raw id so the operator can spot the
 * orphaned binding). */
function resolveSlackChannelName(
  channelId: string | undefined,
  res: AppIntegrationsResponse,
): string | undefined {
  if (!channelId) return undefined
  if (!res.slack.connected) return undefined
  const match = res.slack.channels.find((c) => c.id === channelId)
  if (!match) return undefined
  return match.isPrivate ? `🔒 ${match.name}` : `#${match.name}`
}

